import { Header, RiskBadge } from "@caint/design-system";
import type { Momento, NivelRisco } from "@caint/engine";
import { Fragment, useMemo, useState } from "react";
import { engine } from "../lib/engine.ts";
import type { ItemMerged } from "../state/useAppState.ts";

export interface RiscosProps {
  items: ItemMerged[];
  momento: Momento;
}

interface ItemRisco {
  item: ItemMerged;
  valor: number;
  nivel: NivelRisco;
}

const NIVEIS: { chave: NivelRisco | "todos"; rotulo: string }[] = [
  { chave: "todos", rotulo: "Todos" },
  { chave: "RC", rotulo: "Crítico" },
  { chave: "RA", rotulo: "Alto" },
  { chave: "RM", rotulo: "Moderado" },
  { chave: "RP", rotulo: "Pequeno" },
];

export function Riscos({ items, momento }: RiscosProps) {
  const [filtro, setFiltro] = useState<NivelRisco | "todos">("todos");

  const riscos = useMemo<ItemRisco[]>(() => {
    return items
      .map((item) => {
        const valor = engine.riscoResidual(item, momento);
        const nivel = engine.nivelRisco(valor);
        return valor != null && nivel ? { item, valor, nivel } : null;
      })
      .filter((x): x is ItemRisco => x !== null)
      .sort((a, b) => b.valor - a.valor);
  }, [items, momento]);

  const noPlano = riscos.filter((r) => r.valor >= 8).length;
  const visiveis = filtro === "todos" ? riscos : riscos.filter((r) => r.nivel === filtro);
  const contagem = (n: NivelRisco) => riscos.filter((r) => r.nivel === n).length;

  return (
    <Fragment>
      <Header title="Riscos" sub={`${riscos.length} itens avaliados · ${noPlano} no plano de ação (≥ 8)`} />
      <div className="m-body">
        <div className="m-seg" style={{ marginBottom: 14, flexWrap: "wrap" }}>
          {NIVEIS.map((n) => (
            <button
              key={n.chave}
              type="button"
              className={filtro === n.chave ? "on" : ""}
              onClick={() => setFiltro(n.chave)}
            >
              {n.rotulo}
              {n.chave !== "todos" ? ` ${contagem(n.chave)}` : ""}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {visiveis.map(({ item, valor }) => (
            <div key={item.id} className="m-card pad">
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span className="chip chip-code" style={{ fontSize: 11 }}>
                  {item.code}
                </span>
                <span className="spec">P×I {engine.riscoInerente(item)}</span>
                <span style={{ marginLeft: "auto" }}>
                  <RiskBadge value={valor} withVal />
                </span>
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.35, color: "var(--text)" }}>{item.pergunta}</div>
              {valor >= 8 && (
                <div style={{ marginTop: 6, fontSize: 12, color: "var(--warning)" }}>
                  ⚠️ Plano de Ação ({valor > 14 ? "15 dias" : "60 dias"})
                </div>
              )}
            </div>
          ))}
          {visiveis.length === 0 && (
            <p className="spec" style={{ textAlign: "center", marginTop: 24 }}>
              Nenhum item neste nível de risco.
            </p>
          )}
        </div>
      </div>
    </Fragment>
  );
}
